import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  Search,
  UploadFile,
  Download,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./FinanceOverview.css";

const emptyForm = {
  id: "",
  employeeName: "",
  department: "",
  designation: "",
  location: "",
  paymentMode: "",
  branchName: "",
  ifscCode: "",
  accountNumber: "",
  netPay: "",
  status: "",
};

const FinanceOverview = () => {
  const [data, setData] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [editIndex, setEditIndex] = useState(null);
  const [search, setSearch] = useState("");

  const openModal = (type, row = null, index = null) => {
    setMode(type);
    setForm(row || emptyForm);
    setEditIndex(index);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setForm(emptyForm);
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (Object.values(form).some((v) => v === "")) {
      alert("All fields are required");
      return;
    }

    if (mode === "add") {
      setData([...data, form]);
    } else {
      const updated = [...data];
      updated[editIndex] = form;
      setData(updated);
    }
    closeModal();
  };

  const handleDelete = (index) => {
    if (window.confirm("Delete this record?")) {
      setData(data.filter((_, i) => i !== index));
    }
  };

  // 🔍 SEARCH
  const filteredData = data.filter((row) =>
    Object.values(row).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  // ⬆️ IMPORT EXCEL
  const handleImport = (e) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws);
      setData(json);
    };
    reader.readAsBinaryString(e.target.files[0]);
  };

  // ⬇️ EXPORT EXCEL
  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FinanceOverview");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer]), "Finance_Overview.xlsx");
  };

  return (
    <div className="finance-wrapper">
      <div className="finance-card">
        <div className="finance-header">
          <h2>Finance Overview</h2>

          <div className="finance-actions">
            <TextField
              placeholder="Search..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <Button component="label" startIcon={<UploadFile />}>
              Import
              <input hidden type="file" onChange={handleImport} />
            </Button>

            <Button onClick={handleExport} startIcon={<Download />}>
              Export
            </Button>

            <Button
              className="add-btn"
              startIcon={<Add />}
              onClick={() => openModal("add")}
            >
              Add
            </Button>
          </div>
        </div>

        <TableContainer component={Paper} className="finance-table">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {Object.keys(emptyForm).map((key) => (
                  <TableCell key={key}>
                    {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                  </TableCell>
                ))}
                <TableCell align="center">ACTION</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.map((row, index) => (
                <TableRow key={index}>
                  {Object.keys(emptyForm).map((key) => (
                    <TableCell key={key}>{row[key]}</TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton onClick={() => openModal("view", row)}>
                      <Visibility />
                    </IconButton>
                    <IconButton
                      color="warning"
                      onClick={() => openModal("edit", row, index)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(index)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!filteredData.length && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* MODAL */}
      <Modal open={open} onClose={closeModal}>
        <Box className="finance-modal">
          <div className="modal-header">
            <h3>{mode.toUpperCase()} FINANCE</h3>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </div>

          <div className="modal-grid">
            {Object.keys(emptyForm).map((key) => (
              <TextField
                key={key}
                label={key.replace(/([A-Z])/g, " $1")}
                name={key}
                value={form[key]}
                onChange={handleChange}
                disabled={mode === "view"}
                size="small"
                required
              />
            ))}
          </div>

          {mode !== "view" && (
            <Button className="save-btn" onClick={handleSave}>
              Save
            </Button>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default FinanceOverview;
